// src/hooks/useStores.js
import { useState, useCallback, useEffect } from 'react';
import { listsApi } from '../api/listsApi';

const STORAGE_KEY = 'shopping_active_list_id';

export const useStores = () => {
  const [lists, setLists] = useState([]);
  const [activeListId, setActiveListId] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? parseInt(saved, 10) : null;
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const activeList = lists.find(list => list.id === activeListId) || lists[0] || null;

  useEffect(() => {
    if (activeList?.id) {
      localStorage.setItem(STORAGE_KEY, activeList.id.toString());
    } else {
      localStorage.removeItem(STORAGE_KEY);
    }
  }, [activeList]);

  const normalizeListName = useCallback((listName) => {
    return listName
      .trim()
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }, []);

  const fetchLists = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await listsApi.fetchLists();
      const loadedLists = data.lists || [];
      setLists(loadedLists);

      if (loadedLists.length === 0) {
        setActiveListId(null);
      } else {
        setActiveListId(prev => {
          if (prev && loadedLists.some(list => list.id === prev)) {
            return prev;
          }
          return loadedLists[0].id;
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchLists();
  }, [fetchLists]);

  const addList = useCallback(async (listName) => {
    const normalizedName = normalizeListName(listName);
    if (!normalizedName) return false;

    const existing = lists.find(list => list.name.toLowerCase() === normalizedName.toLowerCase());
    if (existing) {
      setActiveListId(existing.id);
      return false;
    }

    const result = await listsApi.createList(normalizedName);
    const newList = result.data;
    setLists(prev => [...prev, newList]);
    setActiveListId(newList.id);
    return true;
  }, [lists, normalizeListName]);

  const updateActiveList = useCallback((listOrNameOrId) => {
    if (!listOrNameOrId) return;

    if (typeof listOrNameOrId === 'object') {
      setActiveListId(listOrNameOrId.id);
      return;
    }

    if (typeof listOrNameOrId === 'number') {
      setActiveListId(listOrNameOrId);
      return;
    }

    const matchingList = lists.find(list => list.name === listOrNameOrId);
    if (matchingList) {
      setActiveListId(matchingList.id);
    }
  }, [lists]);

  const listExists = useCallback((listName) => {
    const trimmedName = listName.trim().toLowerCase();
    return lists.some(list => list.name.toLowerCase() === trimmedName);
  }, [lists]);

  const filterLists = useCallback((searchTerm) => {
    if (!searchTerm.trim()) return lists;
    
    const term = searchTerm.toLowerCase().trim();
    return lists.filter(list =>
      list.name.toLowerCase().includes(term)
    );
  }, [lists]);

  const formatList = useCallback((list) => {
    if (!list) return '';
    return typeof list === 'string' ? list : list.name;
  }, []);

  const updateList = useCallback(async (listId, name) => {
    const normalizedName = normalizeListName(name);
    const result = await listsApi.updateList(listId, normalizedName);
    const updatedList = result.data;

    setLists(prev => prev.map(list => (
      list.id === updatedList.id ? { ...list, ...updatedList } : list
    )));

    return updatedList;
  }, [normalizeListName]);

  const deleteList = useCallback(async (listId) => {
    await listsApi.deleteList(listId);
    setLists(prev => {
      const nextLists = prev.filter(list => list.id !== listId);
      if (activeListId === listId) {
        setActiveListId(nextLists[0]?.id || null);
      }
      return nextLists;
    });
  }, [activeListId]);

  const syncListsWithAPI = useCallback(async () => {
    await fetchLists();
  }, [fetchLists]);

  return {
    lists,
    activeList,
    activeListId: activeList?.id || null,
    loading,
    error,
    addList,
    updateList,
    deleteList,
    updateActiveList,
    listExists,
    filterLists,
    formatList,
    fetchLists,
    syncListsWithAPI
  };
};